// PrintCredential.jsx
import React from 'react';
import ReactDOMServer from 'react-dom/server';
import { toPng } from 'html-to-image';
import jsPDF from 'jspdf';

const PrintCredential = {
  // Nueva función para imprimir múltiples credenciales
  printMultipleCredentials: async (
    credenciales,
    colorConfigs,
    getLogo,
    isPacksysGroup,
    shouldShowRepse,
    getRepse,
    onConfirmSent
  ) => {
    // Mostrar loading
    const loadingDiv = document.createElement('div');
    loadingDiv.style.position = 'fixed';
    loadingDiv.style.top = '0';
    loadingDiv.style.left = '0';
    loadingDiv.style.width = '100%';
    loadingDiv.style.height = '100%';
    loadingDiv.style.backgroundColor = 'rgba(0,0,0,0.8)';
    loadingDiv.style.zIndex = '9999';
    loadingDiv.style.display = 'flex';
    loadingDiv.style.alignItems = 'center';
    loadingDiv.style.justifyContent = 'center';
    loadingDiv.style.color = 'white';
    loadingDiv.style.fontSize = '18px';
    loadingDiv.style.fontFamily = 'Arial, sans-serif';
    loadingDiv.innerHTML = `
      <div style="text-align:center; background: rgba(0,0,0,0.9); padding: 30px; border-radius: 16px;">
        <div style="border:4px solid rgba(255,255,255,0.3); border-top:4px solid #fff; border-radius:50%; width:50px; height:50px; animation: spin 1s linear infinite; margin:0 auto 20px;"></div>
        <div>Generando PDF, por favor espere...</div>
        <div style="font-size:12px; color:#ccc; margin-top:10px;">Esto puede tomar unos segundos</div>
      </div>
      <style>
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      </style>
    `;
    document.body.appendChild(loadingDiv);

    try {
      const isGuatemala = (credencial) => {
        if (!credencial) return false;
        const localidad = credencial.localidad || '';
        const acceso = credencial.acceso || '';
        return (
          localidad === 'LOC_DHL_GUATEMALA' || acceso === 'Almacén Guatemala'
        );
      };

      // Función para generar puntos (fuera del componente)
      const generateDots = () => {
        const newDots = [];
        for (let i = 0; i < 60; i++) {
          newDots.push({
            id: i,
            x: Math.random() * 100,
            y: Math.random() * 100,
            size: Math.random() * 6 + 1,
          });
        }
        return newDots;
      };

      // Renderizar una sola credencial (tamaño original)
      const renderSingleCredential = (credencial, colorConfig, key) => {
        const dots = generateDots();
        const bgColor = colorConfig.normal;
        const accentDark = colorConfig.fuerte;
        const accentLight = colorConfig.claro;
        const accesoTextColor =
          credencial?.color === 'MORADO' ? accentDark : 'white';
        const accesoBgColor =
          credencial?.color === 'MORADO' ? accentLight : bgColor;
        const isPacksys = isPacksysGroup(credencial);
        const showRepse = shouldShowRepse(credencial);
        const repseValue = getRepse(credencial);

        const Diamond = ({ size = 6, color, animate = false }) => (
          <div
            style={{
              width: `${size}px`,
              height: `${size}px`,
              transform: 'rotate(45deg)',
              backgroundColor: color,
              boxShadow: animate ? `0 0 8px ${color}` : 'none',
            }}
          />
        );

        return (
          <div
            key={key}
            style={{
              display: 'flex',
              gap: '0',
              background: 'white',
              alignItems: 'flex-start',
            }}
          >
            {/* FRONTAL */}
            <div
              style={{
                width: '340px',
                height: '540px',
                background: `linear-gradient(135deg, ${bgColor} 0%, ${accentDark} 100%)`,
                border: '2px solid gray',
                overflow: 'hidden',
                position: 'relative',
              }}
            >
              {/* Fondo de puntos */}
              <div
                style={{
                  position: 'absolute',
                  inset: 0,
                  overflow: 'hidden',
                  pointerEvents: 'none',
                }}
              >
                {dots.map((dot) => (
                  <div
                    key={dot.id}
                    style={{
                      position: 'absolute',
                      left: `${dot.x}%`,
                      top: `${dot.y}%`,
                      width: `${dot.size}px`,
                      height: `${dot.size}px`,
                      backgroundColor: accentLight,
                      borderRadius: '50%',
                      opacity: 0.2 + Math.random() * 0.3,
                    }}
                  />
                ))}
              </div>

              {/* ROMBOS DECORATIVOS */}
              <div
                style={{
                  position: 'absolute',
                  top: '16px',
                  left: '16px',
                  display: 'flex',
                  gap: '4px',
                }}
              >
                <Diamond size={8} color={accentLight} animate />
                <Diamond size={12} color="white" />
                <Diamond size={6} color={accentLight} animate />
              </div>
              <div
                style={{
                  position: 'absolute',
                  top: '16px',
                  right: '16px',
                  display: 'flex',
                  gap: '4px',
                }}
              >
                <Diamond size={6} color={accentLight} animate />
                <Diamond size={12} color="white" />
                <Diamond size={8} color={accentLight} animate />
              </div>
              <div
                style={{
                  position: 'absolute',
                  bottom: '16px',
                  left: '16px',
                  display: 'flex',
                  gap: '4px',
                }}
              >
                <Diamond size={8} color={accentLight} animate />
                <Diamond size={10} color="white" />
                <Diamond size={6} color={accentLight} animate />
              </div>
              <div
                style={{
                  position: 'absolute',
                  bottom: '16px',
                  right: '16px',
                  display: 'flex',
                  gap: '4px',
                }}
              >
                <Diamond size={6} color={accentLight} animate />
                <Diamond size={10} color="white" />
                <Diamond size={8} color={accentLight} animate />
              </div>

              <div
                style={{
                  position: 'absolute',
                  left: '16px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '8px',
                }}
              >
                {[...Array(5)].map((_, i) => (
                  <Diamond
                    key={i}
                    size={5}
                    color="white"
                    animate={i % 2 === 0}
                  />
                ))}
              </div>

              <div
                style={{
                  position: 'absolute',
                  right: '16px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '8px',
                }}
              >
                {[...Array(5)].map((_, i) => (
                  <Diamond
                    key={i}
                    size={5}
                    color="white"
                    animate={i % 2 === 0}
                  />
                ))}
              </div>

              {/* ROMBO CENTRAL GRANDE */}
              <div
                style={{
                  position: 'absolute',
                  left: '50%',
                  top: '50%',
                  transform: 'translate(-50%, -50%) rotate(45deg)',
                  width: '280px',
                  height: '280px',
                  border: `30px solid ${accentLight}`,
                  opacity: 0.1,
                  pointerEvents: 'none',
                }}
              />

              {/* Efecto de brillo superior */}
              <div
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  height: '128px',
                  background:
                    'linear-gradient(to bottom, rgba(255,255,255,0.2), transparent)',
                  pointerEvents: 'none',
                }}
              />

              {/* Contenido frontal */}
              <div
                style={{
                  position: 'relative',
                  padding: '24px',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  textAlign: 'center',
                  zIndex: 10,
                  height: '100%',
                }}
              >
                {/* Logo ovalado */}
                <div
                  style={{
                    position: 'absolute',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    width: '226px',
                    height: '80px',
                    background: 'white',
                    borderRadius: '0 0 12px 12px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: '0 10px 15px -3px rgba(0,0,0,0.1)',
                    zIndex: 10,
                    top: '-4px',
                  }}
                >
                  <img
                    src="/media/PACKSYS.png"
                    alt="Packsys"
                    style={{
                      height: '55px',
                      objectFit: 'contain',
                      padding: '2px',
                    }}
                  />
                </div>

                {/* Acceso */}
                <div
                  style={{
                    position: 'relative',
                    marginTop: '72px',
                    marginBottom: '18px',
                    zIndex: 10,
                  }}
                >
                  <div
                    style={{
                      position: 'absolute',
                      inset: 0,
                      borderRadius: '9999px',
                      filter: 'blur(4px)',
                      background: accentDark,
                      opacity: 0.5,
                    }}
                  />
                  <div
                    style={{
                      position: 'relative',
                      backgroundColor: accesoBgColor,
                      color: accesoTextColor,
                      padding: '8px 24px',
                      borderRadius: '9999px',
                      fontWeight: 'bold',
                      fontSize: '24px',
                      border: `2px solid ${accentDark}`,
                      boxShadow: '0 10px 15px -3px rgba(0,0,0,0.1)',
                    }}
                  >
                    {credencial.acceso || 'ACCESO'}
                  </div>
                </div>

                {/* Foto */}
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '16px',
                    marginBottom: '16px',
                    position: 'relative',
                    zIndex: 10,
                  }}
                >
                  <div style={{ position: 'relative' }}>
                    <div
                      style={{
                        position: 'absolute',
                        inset: '-4px',
                        background: 'rgba(255,255,255,0.3)',
                        borderRadius: '8px',
                        filter: 'blur(4px)',
                      }}
                    />
                    <div
                      style={{
                        position: 'relative',
                        width: '172px',
                        height: '172px',
                        overflow: 'hidden',
                        borderRadius: '8px',
                        border: '4px solid white',
                        boxShadow: '0 20px 25px -5px rgba(0,0,0,0.2)',
                      }}
                    >
                      {credencial.foto_url && credencial.foto_url !== 'null' ? (
                        <img
                          src={credencial.foto_url}
                          alt={credencial.nombre_completo}
                          style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                          }}
                          crossOrigin="anonymous"
                        />
                      ) : (
                        <img
                          src="/media/Credencial_Default.jpg"
                          alt={credencial.nombre_completo}
                          style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                          }}
                          crossOrigin="anonymous"
                        />
                      )}
                    </div>
                  </div>
                </div>

                {/* Nombre */}
                <div
                  style={{
                    color: 'white',
                    marginBottom: '4px',
                    position: 'relative',
                    zIndex: 10,
                  }}
                >
                  <div
                    style={{
                      fontWeight: 'bold',
                      fontSize: '24px',
                      letterSpacing: '0.025em',
                      textShadow: '0 2px 4px rgba(0,0,0,0.2)',
                    }}
                  >
                    {credencial.nombres}
                  </div>
                  <div
                    style={{
                      fontWeight: 'bold',
                      fontSize: '24px',
                      letterSpacing: '0.025em',
                      textShadow: '0 2px 4px rgba(0,0,0,0.2)',
                    }}
                  >
                    {credencial.apellido_paterno} {credencial.apellido_materno}
                  </div>
                </div>

                {/* Puesto */}
                <div
                  style={{
                    position: 'relative',
                    marginTop: '4px',
                    marginBottom: '4px',
                    zIndex: 10,
                  }}
                >
                  <div
                    style={{
                      color: 'rgba(255,255,255,0.9)',
                      fontSize: '13px',
                      fontWeight: '500',
                      padding: '4px 16px',
                      borderRadius: '9999px',
                      background: 'rgba(255,255,255,0.3)',
                      backdropFilter: 'blur(4px)',
                      display: 'inline-block',
                    }}
                  >
                    {credencial.puesto || 'Puesto no especificado'}
                  </div>
                </div>

                {/* Empresa */}
                <div
                  style={{
                    color: 'rgba(255,255,255,0.8)',
                    fontSize: '12px',
                    fontWeight: '500',
                    letterSpacing: '0.05em',
                    textTransform: 'uppercase',
                    marginTop: '4px',
                    zIndex: 10,
                  }}
                >
                  {isGuatemala(credencial)
                    ? 'DHL'
                    : credencial.razon_social || 'PACKSYS MEXICO S.A DE C.V.'}
                </div>
              </div>
            </div>

            {/* Línea de corte */}
            <div
              style={{
                width: '2px',
                height: '540px',
                background:
                  'repeating-linear-gradient(180deg, #9ca3af, #9ca3af 10px, transparent 10px, transparent 20px)',
                margin: '0 10px',
              }}
            />

            {/* TRASERA */}
            <div
              style={{
                width: '340px',
                height: '540px',
                background: 'white',
                border: '2px solid gray',
                overflow: 'hidden',
                position: 'relative',
              }}
            >
              <div
                style={{
                  position: 'absolute',
                  inset: 0,
                  opacity: 0.05,
                  pointerEvents: 'none',
                }}
              >
                <div
                  style={{
                    width: '100%',
                    height: '100%',
                    backgroundImage: `radial-gradient(circle at 2px 2px, ${accentDark} 1px, transparent 1px)`,
                    backgroundSize: '20px 20px',
                  }}
                />
              </div>

              {/* ROMBOS EN TRASERA */}
              <div
                style={{
                  position: 'absolute',
                  top: '16px',
                  left: '16px',
                  display: 'flex',
                  gap: '4px',
                }}
              >
                <Diamond size={6} color={accentDark} animate />
                <Diamond size={8} color={accentDark} />
                <Diamond size={6} color={accentDark} animate />
              </div>
              <div
                style={{
                  position: 'absolute',
                  top: '16px',
                  right: '16px',
                  display: 'flex',
                  gap: '4px',
                }}
              >
                <Diamond size={6} color={accentDark} animate />
                <Diamond size={8} color={accentDark} />
                <Diamond size={6} color={accentDark} animate />
              </div>
              <div
                style={{
                  position: 'absolute',
                  bottom: '16px',
                  left: '16px',
                  display: 'flex',
                  gap: '4px',
                }}
              >
                <Diamond size={6} color={accentDark} animate />
                <Diamond size={8} color={accentDark} />
                <Diamond size={6} color={accentDark} animate />
              </div>
              <div
                style={{
                  position: 'absolute',
                  bottom: '16px',
                  right: '16px',
                  display: 'flex',
                  gap: '4px',
                }}
              >
                <Diamond size={6} color={accentDark} animate />
                <Diamond size={8} color={accentDark} />
                <Diamond size={6} color={accentDark} animate />
              </div>

              <div
                style={{
                  position: 'absolute',
                  left: '50%',
                  top: '50%',
                  transform: 'translate(-50%, -50%) rotate(45deg)',
                  width: '280px',
                  height: '280px',
                  border: `30px solid ${accentLight}`,
                  opacity: 0.1,
                  pointerEvents: 'none',
                }}
              />

              <div
                style={{
                  padding: '24px',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  textAlign: 'center',
                  height: '100%',
                  position: 'relative',
                  zIndex: 10,
                }}
              >
                <div style={{ marginBottom: '24px' }}>
                  <img
                    src={getLogo(credencial)}
                    alt="Logo empresa"
                    style={{
                      height: '64px',
                      objectFit: 'contain',
                      position: 'relative',
                    }}
                  />
                </div>

                {isPacksys ? (
                  <div style={{ textAlign: 'left', width: '100%' }}>
                    {!isGuatemala(credencial) && (
                      <p
                        style={{
                          color: '#4b5563',
                          fontSize: '13px',
                          lineHeight: '1.5',
                          marginBottom: '12px',
                        }}
                      >
                        La siguiente credencial acredita al titular como
                        trabajador asignado a la empresa:
                      </p>
                    )}

                    <div
                      style={{
                        background: 'linear-gradient(to right, #f9fafb, white)',
                        padding: '12px',
                        borderRadius: '8px',
                        borderLeft: `4px solid ${accentDark}`,
                        border: `2px solid ${accentDark}`,
                        marginBottom: '12px',
                        textAlign: 'center',
                      }}
                    >
                      <p
                        style={{
                          fontWeight: 'bold',
                          fontSize: '15px',
                          color: accentDark,
                        }}
                      >
                        {isGuatemala(credencial)
                          ? 'DHL_GUATEMALA'
                          : credencial.razon_social ||
                            'PACKSYS MEXICO S.A DE C.V.'}
                      </p>
                    </div>

                    {isGuatemala(credencial) ? (
                      // Versión Guatemala: CUI y NIT
                      <>
                        <div
                          style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            padding: '8px 12px',
                            background: '#f9fafb',
                            borderRadius: '8px',
                            marginBottom: '8px',
                          }}
                        >
                          <span
                            style={{
                              color: '#6b7280',
                              fontSize: '12px',
                              fontWeight: 'bold',
                              textTransform: 'uppercase',
                            }}
                          >
                            CUI:
                          </span>
                          <span
                            style={{ fontWeight: '500', color: accentDark }}
                          >
                            {credencial.registro_patronal || '3544196920101'}
                          </span>
                        </div>
                        <div
                          style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            padding: '8px 12px',
                            background: '#f9fafb',
                            borderRadius: '8px',
                          }}
                        >
                          <span
                            style={{
                              color: '#6b7280',
                              fontSize: '12px',
                              fontWeight: 'bold',
                              textTransform: 'uppercase',
                            }}
                          >
                            NIT:
                          </span>
                          <span
                            style={{ fontWeight: '500', color: accentDark }}
                          >
                            {credencial.nss || 'No especificado'}
                          </span>
                        </div>
                      </>
                    ) : (
                      // Versión normal: Registro Patronal y NSS
                      <>
                        <div
                          style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            padding: '8px 12px',
                            background: '#f9fafb',
                            borderRadius: '8px',
                            marginBottom: '8px',
                          }}
                        >
                          <span
                            style={{
                              color: '#6b7280',
                              fontSize: '12px',
                              fontWeight: 'bold',
                              textTransform: 'uppercase',
                            }}
                          >
                            Registro Patronal:
                          </span>
                          <span
                            style={{ fontWeight: '500', color: accentDark }}
                          >
                            {credencial.registro_patronal || 'No especificado'}
                          </span>
                        </div>
                        <div
                          style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            padding: '8px 12px',
                            background: '#f9fafb',
                            borderRadius: '8px',
                          }}
                        >
                          <span
                            style={{
                              color: '#6b7280',
                              fontSize: '12px',
                              fontWeight: 'bold',
                              textTransform: 'uppercase',
                            }}
                          >
                            NSS:
                          </span>
                          <span
                            style={{ fontWeight: '500', color: accentDark }}
                          >
                            {credencial.nss || 'No especificado'}
                          </span>
                        </div>
                      </>
                    )}
                  </div>
                ) : (
                  <div style={{ textAlign: 'center', width: '100%' }}>
                    <div
                      style={{
                        background: 'linear-gradient(to right, #f9fafb, white)',
                        padding: '8px',
                        border: `2px solid ${accentDark}`,
                        borderRadius: '12px',
                        marginBottom: '12px',
                      }}
                    >
                      <p
                        style={{
                          fontWeight: 'bold',
                          fontSize: '12px',
                          textTransform: 'uppercase',
                          letterSpacing: '0.05em',
                          color: accentDark,
                        }}
                      >
                        Registro Patronal
                      </p>
                      <p
                        style={{
                          marginTop: '4px',
                          color: '#374151',
                          fontWeight: '500',
                        }}
                      >
                        {credencial.registro_patronal || 'No especificado'}
                      </p>
                    </div>

                    {showRepse && repseValue && (
                      <div
                        style={{
                          background:
                            'linear-gradient(to right, #f9fafb, white)',
                          padding: '8px',
                          border: `2px solid ${accentDark}`,
                          borderRadius: '12px',
                          marginBottom: '12px',
                        }}
                      >
                        <p
                          style={{
                            fontWeight: 'bold',
                            fontSize: '12px',
                            textTransform: 'uppercase',
                            letterSpacing: '0.05em',
                            color: accentDark,
                          }}
                        >
                          REPSE
                        </p>
                        <p
                          style={{
                            marginTop: '4px',
                            color: '#374151',
                            fontFamily: 'monospace',
                            fontSize: '11px',
                            wordBreak: 'break-all',
                          }}
                        >
                          {repseValue}
                        </p>
                      </div>
                    )}

                    <div
                      style={{
                        background: 'linear-gradient(to right, #f9fafb, white)',
                        padding: '8px',
                        border: `2px solid ${accentDark}`,
                        borderRadius: '12px',
                        marginBottom: '12px',
                      }}
                    >
                      <p
                        style={{
                          fontWeight: 'bold',
                          fontSize: '12px',
                          textTransform: 'uppercase',
                          letterSpacing: '0.05em',
                          color: accentDark,
                        }}
                      >
                        NSS
                      </p>
                      <p
                        style={{
                          marginTop: '4px',
                          color: '#374151',
                          fontWeight: '500',
                        }}
                      >
                        {credencial.nss || 'No especificado'}
                      </p>
                    </div>

                    <div
                      style={{
                        marginTop: '16px',
                        paddingTop: '12px',
                        borderTop: '2px solid #c9ced8',
                      }}
                    >
                      <p
                        style={{
                          color: '#778194',
                          fontSize: '10px',
                          fontWeight: 'bold',
                          letterSpacing: '0.05em',
                        }}
                      >
                        Personal asignado a Packsys
                      </p>
                    </div>
                  </div>
                )}

                <div
                  style={{
                    display: 'flex',
                    gap: '12px',
                    marginTop: 'auto',
                    paddingTop: '16px',
                  }}
                >
                  {[...Array(3)].map((_, i) => (
                    <div
                      key={i}
                      style={{
                        width: '20px',
                        height: '20px',
                        transform: 'rotate(45deg)',
                        border: `2px solid ${accentDark}`,
                      }}
                    />
                  ))}
                </div>
              </div>
            </div>
          </div>
        );
      };

      // Determinar layout según cantidad de credenciales (1-4)
      const credsToPrint = credenciales.slice(0, 4);
      const totalCreds = credsToPrint.length;

      // Crear el layout con grid
      const PrintLayout = () => {
        // Definir posiciones en grid 2x2
        // Posición 1: arriba izquierda, Posición 2: arriba derecha
        // Posición 3: abajo izquierda, Posición 4: abajo derecha
        const gridStyle = {
          display: 'grid',
          gridTemplateColumns: 'auto auto',
          gridTemplateRows: 'auto auto',
          gap: '20px',
          background: 'white',
          padding: '20px',
          width: 'fit-content',
          margin: '0 auto',
        };

        // Elementos del grid
        const items = [];

        // Posición 1 (arriba izquierda) - siempre presente si hay al menos 1
        if (totalCreds >= 1) {
          items.push(
            <div
              key="pos1"
              style={{ justifySelf: 'center', alignSelf: 'center' }}
            >
              {renderSingleCredential(credsToPrint[0], colorConfigs[0], 0)}
            </div>
          );
        } else {
          items.push(<div key="pos1"></div>);
        }

        // Posición 2 (arriba derecha) - presente si hay al menos 2
        if (totalCreds >= 2) {
          items.push(
            <div
              key="pos2"
              style={{ justifySelf: 'center', alignSelf: 'center' }}
            >
              {renderSingleCredential(credsToPrint[1], colorConfigs[1], 1)}
            </div>
          );
        } else {
          items.push(<div key="pos2"></div>);
        }

        // Posición 3 (abajo izquierda) - presente si hay al menos 3
        if (totalCreds >= 3) {
          items.push(
            <div
              key="pos3"
              style={{ justifySelf: 'center', alignSelf: 'center' }}
            >
              {renderSingleCredential(credsToPrint[2], colorConfigs[2], 2)}
            </div>
          );
        } else {
          items.push(<div key="pos3"></div>);
        }

        // Posición 4 (abajo derecha) - presente si hay al menos 4
        if (totalCreds >= 4) {
          items.push(
            <div
              key="pos4"
              style={{ justifySelf: 'center', alignSelf: 'center' }}
            >
              {renderSingleCredential(credsToPrint[3], colorConfigs[3], 3)}
            </div>
          );
        } else {
          items.push(<div key="pos4"></div>);
        }

        return (
          <div id="multiple-credentials-container" style={gridStyle}>
            {items}
          </div>
        );
      };

      // Renderizar el componente a HTML
      const printHtml = ReactDOMServer.renderToString(<PrintLayout />);

      // Crear un contenedor para imprimir
      const printContainer = document.createElement('div');
      printContainer.style.position = 'fixed';
      printContainer.style.top = '-9999px';
      printContainer.style.left = '-9999px';
      printContainer.innerHTML = printHtml;
      document.body.appendChild(printContainer);

      // Esperar a que las imágenes se carguen
      await new Promise((resolve) => setTimeout(resolve, 1500));

      // Seleccionar el contenedor principal
      const element = printContainer.querySelector(
        '#multiple-credentials-container'
      );

      if (!element) {
        throw new Error('No se pudo encontrar el elemento para renderizar');
      }

      // Configurar tamaño exacto para impresión
      const dataUrl = await toPng(element, {
        quality: 1,
        pixelRatio: 3,
        backgroundColor: '#ffffff',
        cacheBust: true,
      });

      // Crear PDF en tamaño carta horizontal
      const pdf = new jsPDF({
        unit: 'mm',
        format: 'letter',
        orientation: 'landscape',
      });

      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();

      // Crear imagen temporal para obtener dimensiones
      const img = new Image();
      await new Promise((resolve, reject) => {
        img.onload = resolve;
        img.onerror = reject;
        img.src = dataUrl;
      });

      // Calcular dimensiones para que ocupe toda la página (margen mínimo)
      const margin = 5;
      let imgWidth = pageWidth - margin * 2;
      let imgHeight = (img.height * imgWidth) / img.width;

      if (imgHeight > pageHeight - margin * 2) {
        imgHeight = pageHeight - margin * 2;
        imgWidth = (img.width * imgHeight) / img.height;
      }

      const xOffset = (pageWidth - imgWidth) / 2;
      const yOffset = (pageHeight - imgHeight) / 2;

      pdf.addImage(dataUrl, 'PNG', xOffset, yOffset, imgWidth, imgHeight);

      const credsCount = credsToPrint.length;
      pdf.save(
        `Credenciales_${credsCount}_${new Date().toISOString().slice(0, 19).replace(/:/g, '-')}.pdf`
      );

      // Marcar como enviadas
      credsToPrint.forEach((credencial) => {
        if (onConfirmSent) {
          onConfirmSent(credencial.id);
        }
      });
    } catch (error) {
      console.error('Error generando PDF:', error);
      alert(
        'Error al generar el PDF: ' + (error.message || 'Intenta de nuevo')
      );
    } finally {
      if (document.body.contains(loadingDiv)) {
        document.body.removeChild(loadingDiv);
      }
    }
  },

  // Método legacy para compatibilidad
  printCredential: async (
    credencial,
    colorConfig,
    getLogo,
    isPacksysGroup,
    shouldShowRepse,
    getRepse
  ) => {
    return PrintCredential.printMultipleCredentials(
      [credencial],
      [colorConfig],
      getLogo,
      isPacksysGroup,
      shouldShowRepse,
      getRepse,
      () => {}
    );
  },
};

export default PrintCredential;
